import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, Sparkles, Mail, MessageCircle } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { useAuth } from '../context/AuthContext';

/**
 * Yataş Grubu — Otomatik Uyarı Sistemi + AI Bütçe Önerisi (PROTOTİP)  /yatas/uyarilar-prototip
 * Backend: /api/yatas-alerts-proto (catalog, backtest, digest, explain). Sidebar'da yok, doğrudan link.
 * Hiçbir uygulama/gönderme eylemi yoktur.
 */

const API = 'https://api.glowytics.com';
const YATAS_GROUP_BRAND_IDS = new Set([
  '44e35dfe-0186-495d-a0cc-db5e7e459ab7', 'dfcf54dd-b00d-434a-be1e-27b6484848eb',
  '7fc8e68d-f2ba-4101-95f7-acece386ab8f', 'ccb2364b-e4e1-4f7f-944a-44cc9a839c6e',
]);

const C = {
  ground: '#F6F8FF', card: '#FFFFFF', line: '#E5E7EB', hair: '#F3F4F6', head: '#FAFBFF',
  ink: '#111827', ink2: '#374151', muted: '#6B7280', faint: '#9CA3AF',
  accent: '#436AEB', accentSoft: '#EEF2FE',
};
const SEV = {
  kritik: { label: 'Kritik', fg: '#B91C1C', bg: '#FEF2F2', bd: '#FECACA' },
  uyari: { label: 'Uyarı', fg: '#B45309', bg: '#FFFBEB', bd: '#FDE68A' },
  bilgi: { label: 'Bilgi', fg: '#475569', bg: '#F1F5F9', bd: '#CBD5E1' },
};
const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
const kart = { background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, boxShadow: '0 1px 2px rgba(16,24,40,.04)' };
const nf = (v, d = 0) => Number(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d });
const tl = (v) => (v == null ? '–' : `₺${nf(v)}`);
const fmtVal = (m) => (m && m.value != null ? `${typeof m.value === 'number' ? nf(m.value, Math.abs(m.value) < 10 && m.value % 1 ? 2 : 0) : m.value}${m.unit ? ` ${m.unit}` : ''}` : '–');
const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(`${d}T00:00:00`);
  return dt.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' });
};
const platLabel = (p) => ({ google: 'Google Ads', meta: 'Meta', tiktok: 'TikTok' }[String(p || '').toLowerCase()] || p || '–');

async function api(path, brandId, opts = {}) {
  const res = await fetch(`${API}/api/yatas-alerts-proto${path}`, {
    credentials: 'include',
    headers: { 'X-Brand-ID': brandId, ...(opts.body ? { 'Content-Type': 'application/json' } : {}) },
    ...opts,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || json.message || `HTTP ${res.status}`);
  return json;
}

function useLoad(fn, deps) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  useEffect(() => {
    let alive = true;
    setState({ data: null, loading: true, error: null });
    fn().then((data) => alive && setState({ data, loading: false, error: null }))
      .catch((e) => alive && setState({ data: null, loading: false, error: e.message }));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

const Spin = ({ text = 'Yükleniyor…' }) => (
  <div style={{ padding: 28, textAlign: 'center', color: C.muted, fontSize: 13 }}>
    <Loader2 size={18} style={{ animation: 'yapspin 1s linear infinite', verticalAlign: 'middle', marginRight: 8 }} />{text}
    <style>{'@keyframes yapspin{to{transform:rotate(360deg)}}'}</style>
  </div>
);
const Err = ({ msg }) => (
  <div style={{ ...kart, padding: 16, color: '#B91C1C', background: '#FEF2F2', borderColor: '#FECACA', fontSize: 13 }}>{msg}</div>
);

function SevBadge({ s }) {
  const v = SEV[s] || SEV.bilgi;
  return <span style={{ fontSize: 11, fontWeight: 700, color: v.fg, background: v.bg, border: `1px solid ${v.bd}`, borderRadius: 6, padding: '2px 7px' }}>{v.label}</span>;
}
const Chip = ({ children, color = C.muted, bg = C.hair }) => (
  <span style={{ fontSize: 11, fontWeight: 600, color, background: bg, borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap' }}>{children}</span>
);
const DisabledBtn = ({ children }) => (
  <button type="button" disabled style={{ border: `1px solid ${C.line}`, background: C.hair, color: C.faint, borderRadius: 8, padding: '7px 12px', fontSize: 12, cursor: 'not-allowed' }}>
    {children} <span style={{ fontSize: 10 }}>(Prototip — uygulanmaz)</span>
  </button>
);

/* ── 1. Özet ───────────────────────────────────────────────────── */
function Summary({ brandId, brandName, backtest, days }) {
  const all = useLoad(() => api(`/backtest?all=1&days=${days}`, brandId), [brandId, days]);
  const s = backtest.summary;
  const rate = (x) => (x.generated ? Math.round((x.suppressed / x.generated) * 100) : 0);
  return (
    <div>
      <div style={{ ...kart, padding: 16, marginBottom: 14, fontSize: 13, color: C.ink2, lineHeight: 1.6 }}>
        <b>Gürültü neden önemli?</b> Kurallar günde onlarca olası uyarı üretir; hepsini göndermek ekibi uyarıya alıştırır.
        Aynı sorun için tekrar eden (cooldown) ve düşük harcamalı uyarılar bastırılır. {brandName} için son {days} günde{' '}
        <b>{nf(s.generated)}</b> uyarı üretildi, <b>{nf(s.emitted)}</b> yayınlandı, <b>{nf(s.suppressed)}</b> bastırıldı
        (<b>%{rate(s)}</b> gürültü azaltma).
      </div>
      {all.loading ? <Spin /> : all.error ? <Err msg={all.error} /> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(250px,1fr))', gap: 12, marginBottom: 16 }}>
          {all.data.brands.map((b) => {
            const sm = b.summary || {};
            const sv = sm.bySeverityEmitted || {};
            return (
              <div key={b.brand?.id} style={{ ...kart, padding: 16, borderColor: b.brand?.id === brandId ? C.accent : C.line }}>
                <div style={{ fontWeight: 700, color: C.ink, fontSize: 15 }}>{b.brand?.name}</div>
                {b.params?.lastDataDate && <div style={{ fontSize: 11, color: C.faint }}>Son veri: {b.params.lastDataDate}</div>}
                {b.access === false || !b.summary ? <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>Erişim yok / veri yok</div> : (
                  <>
                    <div style={{ display: 'flex', gap: 14, marginTop: 10 }}>
                      {[['Üretilen', sm.generated], ['Yayınlanan', sm.emitted], ['Bastırılan', sm.suppressed]].map(([l, v]) => (
                        <div key={l}><div style={{ fontSize: 22, fontWeight: 700, color: C.ink }}>{nf(v)}</div><div style={{ fontSize: 11, color: C.muted }}>{l}</div></div>
                      ))}
                    </div>
                    <div style={{ fontSize: 12, color: C.ink2, marginTop: 8 }}>
                      Günlük ort.: {nf(sm.avgGeneratedPerDay, 1)} üretilen / <b>{nf(sm.avgEmittedPerDay, 1)}</b> yayınlanan · bastırma %{rate(sm)}
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                      {Object.keys(SEV).map((k) => <span key={k} style={{ fontSize: 11, fontWeight: 600, color: SEV[k].fg, background: SEV[k].bg, borderRadius: 6, padding: '2px 7px' }}>{SEV[k].label}: {nf(sv[k])}</span>)}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div style={{ ...kart, padding: 16 }}>
        <div style={{ fontWeight: 600, color: C.ink, marginBottom: 10 }}>{brandName} — günlük üretilen vs yayınlanan</div>
        <div style={{ height: 240 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={s.perDay || []}>
              <CartesianGrid stroke={C.hair} vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: C.faint }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 11, fill: C.faint }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 10, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="generated" name="Üretilen" fill="#CBD5E1" radius={[3, 3, 0, 0]} />
              <Bar dataKey="emitted" name="Yayınlanan" fill={C.accent} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

/* ── 2. Katalog ────────────────────────────────────────────────── */
function Catalog({ brandId, backtest }) {
  const cat = useLoad(() => api('/catalog', brandId), [brandId]);
  if (cat.loading) return <Spin />;
  if (cat.error) return <Err msg={cat.error} />;
  const { global: g, rules } = cat.data;
  const byRule = Object.fromEntries((backtest.summary.byRule || []).map((r) => [r.ruleId, r]));
  return (
    <div>
      <div style={{ ...kart, padding: 14, marginBottom: 14, fontSize: 13, color: C.ink2, lineHeight: 1.7 }}>
        {g.noiseNote && <div><b>Gürültü:</b> {g.noiseNote}</div>}
        {g.dedup && <div><b>Tekrar önleme:</b> {typeof g.dedup === 'string' ? g.dedup : JSON.stringify(g.dedup)}</div>}
        {g.timing && <div><b>Zamanlama:</b> {typeof g.timing === 'string' ? g.timing : JSON.stringify(g.timing)}</div>}
        {g.minDailySpendTL != null && <div><b>Asgari günlük harcama:</b> {tl(g.minDailySpendTL)}</div>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(360px,1fr))', gap: 12 }}>
        {rules.map((r) => {
          const c = byRule[r.id];
          const noData = r.status === 'veri_yok';
          return (
            <div key={r.id} style={{ ...kart, padding: 16, opacity: noData ? 0.95 : 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                <div style={{ fontWeight: 700, color: C.ink, fontSize: 14 }}>{r.name}</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <SevBadge s={r.severity} />
                  <Chip color={noData ? '#B45309' : '#15803D'} bg={noData ? '#FFFBEB' : '#ECFDF3'}>{noData ? 'Veri yok' : 'Canlı'}</Chip>
                </div>
              </div>
              <div style={{ fontSize: 13, color: C.ink2, marginTop: 8, lineHeight: 1.55 }}>{r.explain101}</div>
              {noData && r.missing && (
                <div style={{ marginTop: 8, padding: '8px 10px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, fontSize: 12, color: '#92400E' }}>
                  <b>Eksik:</b> {r.missing}
                </div>
              )}
              <div style={{ marginTop: 10, fontSize: 12, color: C.muted, lineHeight: 1.7 }}>
                {r.thresholds && Object.keys(r.thresholds).length > 0 && (
                  <div><b>Eşikler:</b> {Object.entries(r.thresholds).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' · ')}</div>
                )}
                <div><b>Veri kaynağı:</b> {r.dataSource}</div>
                {r.severityNote && <div><b>Önem notu:</b> {r.severityNote}</div>}
                <div><b>Cooldown:</b> {r.cooldownDays} gün</div>
                {r.ruleSuggestion && <div><b>Kural önerisi:</b> {r.ruleSuggestion}</div>}
              </div>
              <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <Chip bg={C.accentSoft} color={C.accent}>Üretilen: {nf(c?.generated)}</Chip>
                <Chip>Yayınlanan: {nf(c?.emitted)}</Chip>
                <Chip>Bastırılan: {nf(c?.suppressed)}</Chip>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── 3. Akış ───────────────────────────────────────────────────── */
function AlertCard({ a }) {
  const sv = SEV[a.severity] || SEV.bilgi;
  const ai = a.ai;
  const srcLabel = { gemini: 'Gemini', cache: 'Önbellek', rule: 'Kural metni' }[ai?.source] || ai?.source;
  return (
    <div style={{ ...kart, padding: 14, borderLeft: `4px solid ${sv.fg}`, opacity: a.suppressed ? 0.55 : 1 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <SevBadge s={a.severity} />
        <Chip>{platLabel(a.platform)}</Chip>
        <Chip>{a.ruleName}</Chip>
        {a.suppressed && <Chip color="#6B7280" bg="#E5E7EB">bastırıldı{a.suppressedBy ? ` (ilk: ${a.suppressedBy})` : ''}</Chip>}
      </div>
      <div style={{ fontWeight: 700, color: C.ink, marginTop: 8, fontSize: 14 }}>{a.headline}</div>
      {a.campaign && <div style={{ fontSize: 12, color: C.muted }}>Kampanya: {a.campaign}</div>}
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 8, fontSize: 12 }}>
        <div><span style={{ color: C.muted }}>{a.measured?.label || 'Ölçülen'}: </span><b style={{ color: sv.fg }}>{fmtVal(a.measured)}</b></div>
        <div><span style={{ color: C.muted }}>{a.threshold?.label || 'Eşik'}: </span><b>{fmtVal(a.threshold)}</b></div>
        {a.impactTL != null && <div><span style={{ color: C.muted }}>Tahmini etki: </span><b>{tl(a.impactTL)}</b></div>}
      </div>
      {a.text && <div style={{ fontSize: 13, color: C.ink2, marginTop: 8, lineHeight: 1.55 }}>{a.text}</div>}
      {a.suggestionRule && (
        <div style={{ fontSize: 12, color: C.ink2, marginTop: 8, background: C.hair, borderRadius: 8, padding: '7px 10px' }}>
          <b>Kural önerisi:</b> {a.suggestionRule}
        </div>
      )}
      {ai && (
        <div style={{ marginTop: 8, background: C.accentSoft, border: `1px solid #D6E0FB`, borderRadius: 8, padding: '9px 11px', fontSize: 13, color: C.ink2, lineHeight: 1.55 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <b style={{ color: C.accent }}><Sparkles size={13} style={{ verticalAlign: -2 }} /> AI açıklaması</b>
            <Chip color={C.accent} bg="#fff">{srcLabel}</Chip>
          </div>
          <div style={{ marginTop: 4 }}>{ai.explanation}</div>
          {ai.suggestion && (
            <div style={{ marginTop: 6 }}><Chip color="#92400E" bg="#FEF3C7">Bütçe önerisi (öneri — uygulanmaz)</Chip><div style={{ marginTop: 4 }}>{ai.suggestion}</div></div>
          )}
        </div>
      )}
    </div>
  );
}

function Feed({ alerts, rules }) {
  const [sev, setSev] = useState('');
  const [rule, setRule] = useState('');
  const [plat, setPlat] = useState('');
  const [showSup, setShowSup] = useState(false);
  const platforms = useMemo(() => [...new Set(alerts.map((a) => a.platform).filter(Boolean))], [alerts]);
  const filtered = alerts.filter((a) => (showSup || !a.suppressed) && (!sev || a.severity === sev) && (!rule || a.ruleId === rule) && (!plat || a.platform === plat));
  const groups = useMemo(() => {
    const m = {};
    filtered.forEach((a) => { (m[a.date] = m[a.date] || []).push(a); });
    return Object.entries(m).sort((x, y) => (x[0] < y[0] ? 1 : -1));
  }, [filtered]);
  const sel = { border: `1px solid ${C.line}`, borderRadius: 8, padding: '6px 10px', fontSize: 13, background: '#fff', color: C.ink2 };
  return (
    <div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <select style={sel} value={sev} onChange={(e) => setSev(e.target.value)}>
          <option value="">Tüm önemler</option>
          {Object.keys(SEV).map((k) => <option key={k} value={k}>{SEV[k].label}</option>)}
        </select>
        <select style={sel} value={rule} onChange={(e) => setRule(e.target.value)}>
          <option value="">Tüm kurallar</option>
          {(rules || []).map((r) => <option key={r.ruleId} value={r.ruleId}>{r.ruleName}</option>)}
        </select>
        <select style={sel} value={plat} onChange={(e) => setPlat(e.target.value)}>
          <option value="">Tüm platformlar</option>
          {platforms.map((p) => <option key={p} value={p}>{platLabel(p)}</option>)}
        </select>
        <label style={{ fontSize: 13, color: C.ink2 }}>
          <input type="checkbox" checked={showSup} onChange={(e) => setShowSup(e.target.checked)} /> Bastırılanları göster
        </label>
        <span style={{ fontSize: 12, color: C.muted }}>{filtered.length} uyarı</span>
      </div>
      {groups.length === 0 && <div style={{ ...kart, padding: 24, textAlign: 'center', color: C.muted, fontSize: 13 }}>Bu filtrelerle uyarı yok.</div>}
      {groups.map(([d, list]) => (
        <div key={d} style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.accent, marginBottom: 8 }}>{fmtDate(d)} <span style={{ color: C.faint, fontWeight: 500 }}>· {list.length}</span></div>
          <div style={{ display: 'grid', gap: 10 }}>{list.map((a) => <AlertCard key={a.id} a={a} />)}</div>
        </div>
      ))}
    </div>
  );
}

/* ── 4. AI ─────────────────────────────────────────────────────── */
function AiLayer({ brandId, onResult }) {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);
  const run = async () => {
    setBusy(true); setErr(null);
    try {
      const j = await api('/explain', brandId, { method: 'POST', body: JSON.stringify({ days: 30, aiDays: 7, max: 40 }) });
      setRes(j); onResult(j.alerts || []);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const ai = res?.ai; const u = ai?.usage;
  return (
    <div style={{ ...kart, padding: 16 }}>
      <div style={{ fontSize: 13, color: C.ink2, marginBottom: 10 }}>
        AI yalnızca açıklama ve bütçe <b>önerisi</b> metni üretir; hiçbir reklam hesabında değişiklik yapılmaz.
      </div>
      <button type="button" onClick={run} disabled={busy} style={{ background: busy ? C.faint : C.accent, color: '#fff', border: 0, borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: busy ? 'wait' : 'pointer' }}>
        <Sparkles size={14} style={{ verticalAlign: -2 }} /> Son 7 günün uyarılarını AI ile açıkla (en fazla 40)
      </button>
      {busy && <Spin text="AI açıklamaları hazırlanıyor (10–30 sn sürebilir)…" />}
      {err && <div style={{ marginTop: 12 }}><Err msg={err} /></div>}
      {ai && (
        <div style={{ marginTop: 14, fontSize: 13, color: C.ink2, lineHeight: 1.8 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Chip bg={C.accentSoft} color={C.accent}>Model: {ai.model || u?.model}</Chip>
            <Chip>Aday: {nf(ai.candidates)}</Chip>
            <Chip>AI: {nf(ai.aiExplained)}</Chip>
            <Chip>Önbellek: {nf(ai.fromCache)}</Chip>
            <Chip>Kural metni: {nf(ai.ruleTextOnly)}</Chip>
          </div>
          {u && (
            <div style={{ marginTop: 8 }}>
              Çağrı: {nf(u.calls)} · Girdi token: {nf(u.promptTokens)} · Çıktı token: {nf(u.outputTokens)} · Toplam: {nf(u.totalTokens)} ·
              Tahmini maliyet: <b>${nf(u.estimatedUsd, 4)}</b>
              {u.priceNote && <div style={{ fontSize: 11, color: C.faint }}>{u.priceNote}</div>}
              {u.error && <div style={{ marginTop: 6, padding: '7px 10px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, color: '#92400E', fontSize: 12 }}>{String(u.error)}</div>}
            </div>
          )}
          <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>Sonuçlar "Uyarı akışı" sekmesindeki ilgili kartlara işlendi.</div>
        </div>
      )}
    </div>
  );
}

/* ── 5. Teslim önizlemesi ──────────────────────────────────────── */
function Delivery({ daily, brandName }) {
  const dates = useMemo(() => [...(daily || [])].sort((a, b) => (a.date < b.date ? 1 : -1)), [daily]);
  const [date, setDate] = useState(dates[0]?.date || '');
  const day = dates.find((d) => d.date === date);
  const dg = day?.digest;
  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <select value={date} onChange={(e) => setDate(e.target.value)} style={{ border: `1px solid ${C.line}`, borderRadius: 8, padding: '6px 10px', fontSize: 13 }}>
          {dates.map((d) => <option key={d.date} value={d.date}>{d.date} ({d.emitted} yayınlanan)</option>)}
        </select>
        <Chip color="#92400E" bg="#FEF3C7">Sadece önizleme — gönderilmez</Chip>
      </div>
      {!dg ? <div style={{ ...kart, padding: 24, textAlign: 'center', color: C.muted, fontSize: 13 }}>Bu gün için özet yok (yayınlanan uyarı yok).</div> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(340px,1fr))', gap: 16 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.ink2, marginBottom: 6 }}><Mail size={14} style={{ verticalAlign: -2 }} /> Mail</div>
            <div style={{ ...kart, overflow: 'hidden' }}>
              <div style={{ padding: '10px 14px', background: C.head, borderBottom: `1px solid ${C.line}`, fontSize: 12, color: C.muted }}>
                Konu: <b style={{ color: C.ink }}>{dg.subject}</b>
              </div>
              <div style={{ padding: 16 }}>
                <div style={{ fontWeight: 700, color: C.ink }}>{dg.brand || brandName} — günlük uyarı özeti</div>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>{fmtDate(dg.date)} · toplam {dg.total}</div>
                {(dg.top || []).map((t) => (
                  <div key={t.alertId} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 0', borderTop: `1px solid ${C.hair}`, fontSize: 13 }}>
                    <SevBadge s={t.severity} />
                    <div><div style={{ color: C.ink }}>{t.title}</div><div style={{ fontSize: 11, color: C.faint }}>{platLabel(t.platform)}{t.campaign ? ` · ${t.campaign}` : ''}</div></div>
                  </div>
                ))}
                {dg.moreCount > 0 && <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>+{dg.moreCount} uyarı daha</div>}
                <div style={{ marginTop: 14 }}><DisabledBtn>Panele git</DisabledBtn></div>
              </div>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.ink2, marginBottom: 6 }}><MessageCircle size={14} style={{ verticalAlign: -2 }} /> WhatsApp</div>
            <div style={{ background: '#E5DDD5', borderRadius: 12, padding: 18, minHeight: 200 }}>
              <div style={{ background: '#DCF8C6', borderRadius: '10px 10px 2px 10px', padding: '10px 12px', maxWidth: 300, marginLeft: 'auto', fontSize: 13, color: '#111', lineHeight: 1.5, boxShadow: '0 1px 1px rgba(0,0,0,.12)' }}>
                <b>{dg.brand || brandName}</b> · {dg.date}<br />
                {dg.total} uyarı{dg.bySeverity ? ` (${Object.keys(SEV).filter((k) => dg.bySeverity[k]).map((k) => `${dg.bySeverity[k]} ${SEV[k].label.toLowerCase()}`).join(', ')})` : ''}
                {(dg.top || []).slice(0, 3).map((t) => (<div key={t.alertId} style={{ marginTop: 4 }}>• {t.title}</div>))}
                {dg.moreCount > 0 && <div style={{ marginTop: 4 }}>+{dg.moreCount} uyarı daha. Detay panelde.</div>}
                <div style={{ fontSize: 10, color: '#667', textAlign: 'right', marginTop: 4 }}>09:00 ✓✓</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Ana ───────────────────────────────────────────────────────── */
const TABS = [['ozet', 'Özet'], ['katalog', 'Uyarı kataloğu'], ['akis', 'Uyarı akışı'], ['ai', 'AI katmanı'], ['teslim', 'Teslim önizlemesi']];

function Inner({ brandId, brandName }) {
  const days = 30;
  const [tab, setTab] = useState('ozet');
  const [overrides, setOverrides] = useState({});
  const bt = useLoad(() => api(`/backtest?days=${days}`, brandId), [brandId]);
  const onAi = useCallback((list) => {
    setOverrides((o) => { const n = { ...o }; list.forEach((a) => { n[a.id] = a; }); return n; });
  }, []);
  const alerts = useMemo(() => (bt.data?.alerts || []).map((a) => (overrides[a.id] ? { ...a, ...overrides[a.id] } : a)), [bt.data, overrides]);
  const p = bt.data?.params;
  return (
    <div style={{ fontFamily: FONT }}>
      <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', color: '#92400E', borderRadius: 10, padding: '10px 14px', fontSize: 13, fontWeight: 600, marginBottom: 16 }}>
        PROTOTİP — reklam hesaplarında hiçbir değişiklik yapılmaz, bildirim gönderilmez
      </div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: C.ink, margin: 0 }}>Otomatik Uyarı Sistemi + AI Bütçe Önerisi</h1>
      <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>
        {brandName}{p ? ` · ${p.from} → ${p.to} · son veri günü: ` : ''}{p && <b style={{ color: C.ink2 }}>{p.lastDataDate}</b>}
        {p && ` · asgari günlük harcama ${tl(p.minDailySpendTL)}`}
      </div>
      {bt.data?.dataAvailability && (
        <div style={{ fontSize: 11, color: C.faint, marginTop: 2 }}>Veri durumu: {Object.entries(bt.data.dataAvailability).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`).join(' · ')}</div>
      )}
      <div style={{ display: 'flex', gap: 4, margin: '18px 0', borderBottom: `1px solid ${C.line}`, flexWrap: 'wrap' }}>
        {TABS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} style={{ background: 'none', border: 0, borderBottom: `2px solid ${tab === k ? C.accent : 'transparent'}`, color: tab === k ? C.accent : C.muted, fontWeight: 600, fontSize: 14, padding: '9px 14px', cursor: 'pointer' }}>{l}</button>
        ))}
      </div>
      {bt.loading ? <Spin text="Geriye dönük test hesaplanıyor…" /> : bt.error ? <Err msg={bt.error} /> : (
        <>
          {tab === 'ozet' && <Summary brandId={brandId} brandName={brandName} backtest={bt.data} days={days} />}
          {tab === 'katalog' && <Catalog brandId={brandId} backtest={bt.data} />}
          {tab === 'akis' && <Feed alerts={alerts} rules={bt.data.summary.byRule} />}
          {tab === 'ai' && <AiLayer brandId={brandId} onResult={onAi} />}
          {tab === 'teslim' && <Delivery daily={bt.data.daily} brandName={brandName} />}
        </>
      )}
    </div>
  );
}

export default function YatasAlertsPrototype() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id;
  if (!brandId || !YATAS_GROUP_BRAND_IDS.has(brandId)) {
    return <div style={{ padding: 32, textAlign: 'center', color: C.faint, fontSize: 14, fontFamily: FONT }}>Bu sayfaya erişim yetkiniz bulunmuyor.</div>;
  }
  return (
    <div style={{ background: C.ground, minHeight: '100%', padding: '28px 32px 48px' }}>
      <div style={{ maxWidth: 1320, margin: '0 auto' }}>
        <Inner key={brandId} brandId={brandId} brandName={selectedBrand?.name} />
      </div>
    </div>
  );
}
